
import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { THEMES, ThemeColor, DEFAULT_THEME_COLOR } from "@/lib/themes";

/** Parse theme HSL channel string like "221.2 83.2% 53.3%". */
function parseHslChannels(primary: string): { h: number; s: number; l: number } | null {
  const m = primary.trim().match(/^([\d.]+)\s+([\d.]+%?)\s+([\d.]+)%?/);
  if (!m) return null;
  return {
    h: parseFloat(m[1]),
    s: parseFloat(m[2]),
    l: parseFloat(m[3]),
  };
}

/** Approx. WCAG relative luminance from HSL (0–1). Saturated violets read darker than raw L%. */
function hslRelativeLuminance(h: number, sPercent: number, lPercent: number): number {
  const s = Math.min(100, Math.max(0, sPercent)) / 100;
  const l = Math.min(100, Math.max(0, lPercent)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  r += m;
  g += m;
  b += m;
  const toLin = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

/** White on primary for indigo/violet; dark text only for truly light yellows/pastels. */
function pickPrimaryForeground(h: number, s: number, l: number): string {
  const Y = hslRelativeLuminance(h, s, l);
  return Y > 0.42 ? "0 0% 12%" : "0 0% 98%";
}

/**
 * Stronger ink for primary-colored text on light surfaces (white chips).
 * Pale accents (sky / lime / gold) get darkened so labels stay readable.
 */
function pickPrimaryInk(h: number, s: number, l: number, isDark: boolean): string {
  const Y = hslRelativeLuminance(h, s, l);
  if (isDark) {
    // On zinc-700 chips: lift pale-dark primaries slightly for contrast
    if (Y < 0.2) return `${h} ${Math.min(s + 8, 90)}% ${Math.min(l + 12, 72)}%`;
    return `${h} ${s}% ${l}%`;
  }
  if (Y <= 0.28) return `${h} ${s}% ${l}%`;
  const targetL = Y > 0.55 ? 30 : Y > 0.4 ? 34 : 38;
  return `${h} ${Math.min(s + 8, 88)}% ${targetL}%`;
}

/**
 * Soft / muted fills + readable primary-foreground derived from the accent.
 * Keeps gains/losses (emerald/rose) untouched — only chrome tokens.
 */
export function deriveThemeTokens(primary: string, isDark: boolean) {
  const parsed = parseHslChannels(primary);
  if (!parsed) {
    return {
      soft: isDark ? "220 30% 16%" : "220 40% 95%",
      muted: isDark ? "220 25% 22%" : "220 35% 91%",
      foreground: "0 0% 98%",
      ink: isDark ? "220 70% 70%" : "220 80% 40%",
    };
  }

  const { h, s, l } = parsed;
  const foreground = pickPrimaryForeground(h, s, l);
  const ink = pickPrimaryInk(h, s, l, isDark);

  if (isDark) {
    return {
      soft: `${h} ${Math.min(s, 42)}% 15%`,
      muted: `${h} ${Math.min(s, 38)}% 21%`,
      foreground,
      ink,
    };
  }

  return {
    soft: `${h} ${Math.min(s, 62)}% 94%`,
    muted: `${h} ${Math.min(s, 52)}% 90%`,
    foreground,
    ink,
  };
}

export function applyThemeColorToDocument(themeColor: ThemeColor) {
  const themeConfig = THEMES[themeColor] || THEMES[DEFAULT_THEME_COLOR];
  if (!themeConfig) return;

  const root = document.documentElement;
  const isDark = root.classList.contains("dark");
  const palette = isDark ? themeConfig.dark : themeConfig.light;
  const derived = deriveThemeTokens(palette.primary, isDark);

  const setVar = (name: string, value: string) => {
    root.style.setProperty(name, value);
  };

  setVar("--primary", palette.primary);
  setVar("--ring", palette.primary);
  setVar("--primary-foreground", derived.foreground);
  setVar("--primary-soft", derived.soft);
  setVar("--primary-muted", derived.muted);
  setVar("--primary-ink", derived.ink);
  setVar("--sidebar-primary", palette.primary);
  setVar("--sidebar-primary-foreground", derived.foreground);
  setVar("--sidebar-ring", palette.primary);

  if (palette.chart) {
    setVar("--chart-1", palette.chart);
  }
}

export function ThemeManager() {
  const { user } = useAuth();

  useEffect(() => {
    const applyTheme = () => {
      let themeColor = DEFAULT_THEME_COLOR;

      if (user?.themeColor) {
        themeColor = user.themeColor as ThemeColor;
        localStorage.setItem("theme_color", themeColor);
      } else {
        const cached = localStorage.getItem("theme_color");
        if (cached && THEMES[cached as ThemeColor]) {
          themeColor = cached as ThemeColor;
        }
      }

      applyThemeColorToDocument(themeColor);
    };

    applyTheme();

    const handleThemeChange = () => applyTheme();
    window.addEventListener("theme-change", handleThemeChange);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          applyTheme();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("theme-change", handleThemeChange);
    };
  }, [user?.themeColor]);

  return null;
}
